<template>
  <Breadcrumb
    v-if="finalItems?.length > 1"
    :items="finalItems"
    :color-mode="colorMode"
    :show-transparent-background="showTransparentBackground"
  />
</template>

<script setup lang="ts">
import { Breadcrumb } from '@hypercodestudio/basler-components';
import type { NavigationItemInterface } from '@hypercodestudio/basler-components';

import type { BreadcrumbList, ListItem, WithContext } from 'schema-dts';
import {
  toDeprecatedColorMode,
  useInjectedColorMode
} from '~/composables/useInjectedColorMode';
import { buildUrlString } from '~/utils/buildUrlString';
import type { BreadcrumbEntry } from '~/types/MinifiedNavigation';

interface Props {
  /** Pre-computed breadcrumb trail from the page API */
  trail?: BreadcrumbEntry[];
  /** Direct items (bypasses trail) - for custom breadcrumbs like search page */
  items?: NavigationItemInterface[];
  /** Show transparent background */
  showTransparentBackground?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  trail: () => [],
  showTransparentBackground: true,
  items: undefined
});

const locale = useLocale();

const breadcrumbEntries = computed(() => props.trail ?? []);

const colorMode = toDeprecatedColorMode(useInjectedColorMode());
const homeEntry: NavigationItemInterface = {
  // XXX: text is not visible for first entry
  text: 'Home',
  link: {
    uri: buildUrlString(locale.value)
  }
};

/**
 * Convert breadcrumb entries to NavigationItemInterface
 */
function toNavItem(
  entry: { slug: string; text: string },
  withAbsolutePath = false
): NavigationItemInterface {
  return {
    text: entry.text,
    link: {
      uri: buildUrlString(
        locale.value,
        entry.slug,
        undefined,
        undefined,
        withAbsolutePath
      )
    },
    isActive: false
  };
}

const navigationItems = computed<NavigationItemInterface[]>(() => [
  homeEntry,
  ...breadcrumbEntries.value.map((entry) => toNavItem(entry, false))
]);

/**
 * Final items to render - either direct items prop or computed from trail
 */
const finalItems = computed<NavigationItemInterface[]>(() => {
  if (props.items && props.items.length > 0) {
    return props.items;
  }
  return navigationItems.value;
});

const breadcrumbList = computed<WithContext<BreadcrumbList>>(() => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: finalItems.value.map<ListItem>((item, index) => ({
    '@type': 'ListItem',
    name: item.text,
    position: index + 1,
    item: typeof item.link?.uri === 'string' ? item.link?.uri : ''
  }))
}));

useHead({
  script: computed(() => {
    if (props.items?.length || finalItems.value.length <= 1) {
      return [];
    }
    return [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(breadcrumbList.value),
        tagPriority: -1
      }
    ];
  })
});
</script>
